// frontend/src/pages/admin/Stats.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../config/axios';
import { useSocket } from '../../contexts/SocketContext';
import {
    RefreshCw, TrendingUp, Users, Swords, CreditCard, Globe, Radio, BarChart3
} from 'lucide-react';
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

// ─── Etiquetas legibles ───────────────────────────────────────────────────────
const TX_TYPE_LABELS = {
    DEPOSIT:                        'Depósito',
    WITHDRAWAL:                     'Retiro',
    TOURNAMENT_ENTRY:               'Entrada Torneo',
    TOURNAMENT_RESERVATION:         'Reserva',
    TOURNAMENT_RESERVATION_RELEASE: 'Dev. Reserva',
    TOURNAMENT_PRIZE:               'Premio',
    ADMIN_ADJUSTMENT:               'Ajuste Admin',
    REFUND:                         'Reembolso',
};

const MATCH_STATUS_LABELS = {
    PENDING:    'Pendiente',
    READY_CHECK:'Ready Check',
    IN_PROGRESS:'En Curso',
    COMPLETED:  'Completado',
    CANCELLED:  'Cancelado',
};

const TX_COLORS    = ['#22c55e','#ef4444','#f59e0b','#a855f7','#60a5fa','#FCE503','#9ca3af','#f97316'];
const MATCH_COLORS = { PENDING:'#6b7280', READY_CHECK:'#f59e0b', IN_PROGRESS:'#22c55e', COMPLETED:'#2284DF', CANCELLED:'#ef4444' };

const COUNTRY_NAMES = {
    AR:'Argentina', BR:'Brasil', CL:'Chile', UY:'Uruguay', PY:'Paraguay',
    PE:'Perú', BO:'Bolivia', EC:'Ecuador', VE:'Venezuela', CO:'Colombia',
    MX:'México', US:'EE.UU.', ES:'España', PT:'Portugal', IT:'Italia',
    DE:'Alemania', FR:'Francia', GB:'Reino Unido', XX:'Desconocido'
};

// ─── Tooltip oscuro ───────────────────────────────────────────────────────────
const DarkTooltip = ({ active, payload, label, formatter }) => {
    if (!active || !payload?.length) return null;
    const val = formatter ? formatter(payload[0].value) : payload[0].value;
    return (
        <div className="rounded-lg px-3 py-2 text-sm shadow-xl"
             style={{ backgroundColor: '#0A1929', border: '1px solid #1e3a5f' }}>
            <p className="text-gray-400 text-xs mb-0.5">{label || payload[0].name}</p>
            <p className="font-semibold text-white">{val}</p>
        </div>
    );
};

// ─── Tarjeta con gráfico ──────────────────────────────────────────────────────
const ChartCard = ({ title, icon: Icon, iconColor, total, subtitle, color, data = [], type = 'line', formatter, loading }) => {
    const GRID = '#1e3a5f', TICK = '#6b7280';
    return (
        <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
            <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                    {Icon && <Icon className="w-4 h-4" style={{ color: iconColor }} />}
                    {title}
                </span>
                <span className="text-2xl font-bold text-white">{loading ? '—' : (total ?? '—')}</span>
            </div>
            {subtitle && <p className="text-xs text-gray-500 -mt-2">{subtitle}</p>}
            <div className="h-[160px]">
                {loading ? (
                    <div className="h-full flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 animate-spin text-gray-600" />
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        {type === 'bar' ? (
                            <BarChart data={data} margin={{ top: 4, right: 0, left: -30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip content={<DarkTooltip formatter={formatter} />} cursor={{ fill: '#ffffff08' }} />
                                <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
                            </BarChart>
                        ) : type === 'area' ? (
                            <AreaChart data={data} margin={{ top: 4, right: 0, left: -30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip content={<DarkTooltip formatter={formatter} />} cursor={{ stroke: '#ffffff20' }} />
                                <Area type="monotone" dataKey="value" stroke={color} fill={`${color}28`} strokeWidth={2} dot={false} />
                            </AreaChart>
                        ) : (
                            <LineChart data={data} margin={{ top: 4, right: 0, left: -30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip content={<DarkTooltip formatter={formatter} />} cursor={{ stroke: '#ffffff20' }} />
                                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

// ─── Página principal ─────────────────────────────────────────────────────────
const Stats = () => {
    const { socket } = useSocket();
    const [metrics, setMetrics]               = useState(null);
    const [loading, setLoading]               = useState(true);
    const [refreshing, setRefreshing]         = useState(false);
    const [liveConnections, setLiveConnections] = useState(null);

    const fetchMetrics = useCallback(async (isManual = false) => {
        if (isManual) setRefreshing(true);
        try {
            const res = await axios.get('/admin/metrics');
            if (res.data.success) {
                setMetrics(res.data.data);
                setLiveConnections(res.data.data.connections);
            }
        } catch (err) {
            console.error('Error al obtener métricas:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

    // Refresh cada 5 min
    useEffect(() => {
        const id = setInterval(() => fetchMetrics(), 5 * 60_000);
        return () => clearInterval(id);
    }, [fetchMetrics]);

    // Socket: conexiones en tiempo real
    useEffect(() => {
        if (!socket) return;
        const handleConn  = (stats) => setLiveConnections(stats);
        const handleCon   = () => socket.emit('request_queue_stats');
        socket.on('admin:connections_update', handleConn);
        return () => socket.off('admin:connections_update', handleConn);
    }, [socket]);

    // Preparar datos para gráficos de distribución
    const txByTypePie = (metrics?.transactionsByType || []).map((t, i) => ({
        name:  TX_TYPE_LABELS[t.type] || t.type,
        value: t.count,
        color: TX_COLORS[i % TX_COLORS.length],
    }));

    const matchStatusBar = (metrics?.matchesByStatus || [])
        .map(m => ({
            name:  MATCH_STATUS_LABELS[m.status] || m.status,
            value: m.count,
            color: MATCH_COLORS[m.status] || '#9ca3af',
        }))
        .sort((a, b) => b.value - a.value);

    return (
        <div className="space-y-6">

            {/* ── Encabezado ──────────────────────────────────────────────── */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Estadísticas</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Últimos 7 días · actualización cada 5 min</p>
                </div>
                <button
                    onClick={() => fetchMetrics(true)}
                    disabled={refreshing}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
                    style={{ backgroundColor: '#122436' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a3a5c'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#122436'}
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Actualizando...' : 'Actualizar'}
                </button>
            </div>

            {/* ── Gráficos de tendencia ────────────────────────────────────── */}
            <div>
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" style={{ color: '#2284DF' }} />
                    Tendencias (últimos 7 días)
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ChartCard
                        title="Registros" icon={Users} iconColor="#2284DF" type="line" color="#2284DF"
                        data={metrics?.registrationsPerDay}
                        total={metrics?.registrationsPerDay?.reduce((s, d) => s + d.value, 0)}
                        subtitle="Nuevos usuarios" loading={loading}
                    />
                    <ChartCard
                        title="Partidas" icon={Swords} iconColor="#FCE503" type="bar" color="#FCE503"
                        data={metrics?.matchesPerDay}
                        total={metrics?.matchesPerDay?.reduce((s, d) => s + d.value, 0)}
                        subtitle="Matches creados" loading={loading}
                    />
                    <ChartCard
                        title="Ingresos" icon={CreditCard} iconColor="#22c55e" type="area" color="#22c55e"
                        data={metrics?.revenuePerDay}
                        total={`${metrics?.revenuePerDay?.reduce((s, d) => s + d.value, 0) ?? 0} T`}
                        subtitle="Tickets depositados"
                        formatter={v => `${v} tickets`} loading={loading}
                    />
                </div>
            </div>

            {/* ── Distribuciones ──────────────────────────────────────────── */}
            <div>
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" style={{ color: '#FCE503' }} />
                    Distribuciones (histórico)
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Transacciones por tipo */}
                    <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
                        <span className="text-sm font-semibold text-gray-400 flex items-center gap-1.5">
                            <CreditCard className="w-4 h-4" style={{ color: '#2284DF' }} />
                            Transacciones por tipo (completadas)
                        </span>
                        {loading ? (
                            <div className="flex items-center justify-center h-[200px]">
                                <RefreshCw className="w-5 h-5 animate-spin text-gray-600" />
                            </div>
                        ) : txByTypePie.length === 0 ? (
                            <div className="flex items-center justify-center h-[200px]">
                                <p className="text-gray-600 text-sm">Sin datos</p>
                            </div>
                        ) : (
                            <div className="h-[200px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={txByTypePie}
                                            cx="50%" cy="50%"
                                            innerRadius={50} outerRadius={80}
                                            dataKey="value"
                                            label={({ name, percent }) =>
                                                percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''
                                            }
                                            labelLine={false}
                                        >
                                            {txByTypePie.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            content={({ active, payload }) =>
                                                active && payload?.length ? (
                                                    <div className="rounded-lg px-3 py-2 text-sm shadow-xl"
                                                         style={{ backgroundColor: '#0A1929', border: '1px solid #1e3a5f' }}>
                                                        <p className="text-gray-400 text-xs">{payload[0].name}</p>
                                                        <p className="font-semibold text-white">{payload[0].value} transacciones</p>
                                                    </div>
                                                ) : null
                                            }
                                        />
                                        <Legend
                                            formatter={(value) => <span style={{ color: '#9ca3af', fontSize: 11 }}>{value}</span>}
                                            iconSize={8}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    {/* Matches por estado */}
                    <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
                        <span className="text-sm font-semibold text-gray-400 flex items-center gap-1.5">
                            <Swords className="w-4 h-4" style={{ color: '#a855f7' }} />
                            Matches por estado (histórico)
                        </span>
                        {loading ? (
                            <div className="flex items-center justify-center h-[200px]">
                                <RefreshCw className="w-5 h-5 animate-spin text-gray-600" />
                            </div>
                        ) : matchStatusBar.length === 0 ? (
                            <div className="flex items-center justify-center h-[200px]">
                                <p className="text-gray-600 text-sm">Sin datos</p>
                            </div>
                        ) : (
                            <div className="h-[200px] flex flex-col justify-center gap-2">
                                {(() => {
                                    const max = Math.max(...matchStatusBar.map(m => m.count), 1);
                                    return matchStatusBar.map(({ name, value, color }) => (
                                        <div key={name} className="flex items-center gap-2">
                                            <span className="text-xs text-gray-400 w-28 shrink-0 truncate">{name}</span>
                                            <div className="flex-1 rounded-full overflow-hidden h-2.5" style={{ backgroundColor: '#122436' }}>
                                                <div
                                                    className="h-full rounded-full transition-all duration-700"
                                                    style={{ width: `${(value / max) * 100}%`, backgroundColor: color }}
                                                />
                                            </div>
                                            <span className="text-xs font-bold text-white w-8 text-right shrink-0">{value}</span>
                                        </div>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Actividad en vivo ────────────────────────────────────────── */}
            <div>
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Radio className="w-4 h-4" style={{ color: '#a855f7' }} />
                    Actividad en tiempo real
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Conexiones + historial */}
                    <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                                <Radio className="w-4 h-4" style={{ color: '#a855f7' }} />
                                Usuarios conectados
                                <span className="flex h-2 w-2 relative ml-1">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                                </span>
                            </span>
                            <span className="text-3xl font-bold text-white">
                                {liveConnections?.current ?? '—'}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 -mt-1">Sparkline últimos 30 min (snaps cada 30 s)</p>
                        <div className="h-[120px]">
                            {(liveConnections?.history?.length ?? 0) > 1 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={liveConnections.history.map(h => ({
                                            t: new Date(h.time).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
                                            count: h.count
                                        }))}
                                        margin={{ top: 4, right: 0, left: -40, bottom: 0 }}
                                    >
                                        <XAxis dataKey="t" tick={{ fill: '#6b7280', fontSize: 9 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                        <YAxis tick={{ fill: '#6b7280', fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                        <Tooltip content={<DarkTooltip formatter={v => `${v} usuarios`} />} />
                                        <Area type="monotone" dataKey="count" stroke="#a855f7" fill="#a855f728" strokeWidth={2} dot={false} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-xs text-gray-600 text-center">El historial se construye<br/>cada 30 s desde el inicio del servidor</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top países */}
                    <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                            <Globe className="w-4 h-4" style={{ color: '#FCE503' }} />
                            Top países · conectados ahora
                        </span>
                        {loading ? (
                            <div className="flex items-center justify-center h-[140px]">
                                <RefreshCw className="w-5 h-5 animate-spin text-gray-600" />
                            </div>
                        ) : (liveConnections?.topCountries?.length ?? 0) === 0 ? (
                            <div className="flex items-center justify-center h-[140px]">
                                <p className="text-xs text-gray-600">Sin usuarios conectados</p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2.5">
                                {(() => {
                                    const max = Math.max(...liveConnections.topCountries.map(c => c.count), 1);
                                    return liveConnections.topCountries.map(({ code, name, count }) => (
                                        <div key={code} className="flex items-center gap-2">
                                            <span className="text-xs text-gray-400 w-24 shrink-0 truncate">{name}</span>
                                            <div className="flex-1 rounded-full overflow-hidden h-2" style={{ backgroundColor: '#122436' }}>
                                                <div
                                                    className="h-full rounded-full transition-all duration-500"
                                                    style={{ width: `${(count / max) * 100}%`, backgroundColor: '#FCE503' }}
                                                />
                                            </div>
                                            <span className="text-xs font-semibold text-white w-6 text-right shrink-0">{count}</span>
                                        </div>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Stats;
